import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../contexts/CartContext';
import Navigation from '../landing/components/Navigation';
import Footer from '../landing/components/Footer';
import { useSound } from '../landing/hooks';
import { useState } from 'react';

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  TWD: 'NT$',
  EUR: '\u20AC',
  JPY: '\u00A5',
  CNY: '\u00A5',
};

function formatPrice(amount: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  if (currency === 'JPY' || currency === 'CNY') {
    return `${symbol}${amount.toLocaleString()}`;
  }
  return `${symbol}${amount.toLocaleString()}`;
}

export default function CartPage() {
  const { t, i18n } = useTranslation('landing');
  const { items, removeItem, updateQuantity, clear, getTotal } = useCart();
  const { playNote, soundEnabled, toggleSound } = useSound();
  const [language, setLanguage] = useState(i18n.language || 'en');
  const currency = language === 'zh-TW' ? 'TWD' : language === 'ja' ? 'JPY' : 'USD';

  const handleSetLanguage = (lang: string) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('i18n_language', lang);
  };

  const navItems = [
    { label: t('nav.services'), href: '/#services', note: 'C5' as const },
    { label: t('nav.products'), href: '/#products', note: 'D5' as const },
    { label: t('nav.portfolio'), href: '/#portfolio', note: 'E5' as const },
    { label: t('nav.industries'), href: '/#industries', note: 'G5' as const },
    { label: t('nav.about'), href: '/#why', note: 'A5' as const },
  ];

  const total = getTotal(currency);
  const lang = language.startsWith('zh') ? 'zh-TW' : language;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navigation
        navItems={navItems}
        language={language}
        onSetLanguage={handleSetLanguage}
        soundEnabled={soundEnabled}
        onToggleSound={toggleSound}
        onPlayNote={playNote}
        contactLabel={t('nav.contact')}
        cartItemCount={items.reduce((s, i) => s + i.quantity, 0)}
      />

      <main className="max-w-4xl mx-auto px-6 pt-32 pb-20">
        <h1 className="text-3xl font-bold mb-8">
          {t('cart.title', 'Shopping Cart')}
        </h1>

        {items.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg mb-6">
              {t('cart.empty', 'Your cart is empty')}
            </p>
            <Link
              to="/products/iot"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('cart.browseProducts', 'Browse Products')}
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-8">
              {items.map(item => (
                <div
                  key={item.productId}
                  className="flex items-center gap-6 bg-gray-900 rounded-xl p-6 border border-gray-800"
                >
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name[lang] || item.name['en']}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold truncate">
                      {item.name[lang] || item.name['en']}
                    </h3>
                    <p className="text-gray-400">
                      {formatPrice(item.pricing[currency] || 0, currency)} each
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                      className="w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
                    >
                      -
                    </button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                      className="w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
                    >
                      +
                    </button>
                  </div>
                  <div className="text-right min-w-[100px]">
                    <p className="font-semibold">
                      {formatPrice((item.pricing[currency] || 0) * item.quantity, currency)}
                    </p>
                  </div>
                  <button
                    onClick={() => removeItem(item.productId)}
                    className="text-gray-500 hover:text-red-400 transition-colors p-2"
                    title={t('cart.remove', 'Remove')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between bg-gray-900 rounded-xl p-6 border border-gray-800">
              <button
                onClick={clear}
                className="text-gray-400 hover:text-red-400 transition-colors text-sm"
              >
                {t('cart.clearAll', 'Clear All')}
              </button>
              <div className="text-right">
                <p className="text-gray-400 text-sm mb-1">{t('cart.total', 'Total')}</p>
                <p className="text-2xl font-bold">{formatPrice(total, currency)}</p>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-4">
              <Link
                to="/products/iot"
                className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
              >
                {t('cart.continueShopping', 'Continue Shopping')}
              </Link>
              <Link
                to="/checkout"
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {t('cart.checkout', 'Proceed to Checkout')}
              </Link>
            </div>
          </>
        )}
      </main>

      <Footer
        companyName={t('footer.companyName')}
        companyNameZh={t('footer.companyNameZh')}
        slogan={t('footer.slogan')}
        copyright={t('footer.copyright')}
        navItems={navItems.map(item => ({ label: item.label, href: item.href }))}
      />
    </div>
  );
}
